import { type CellInput } from './contract/cells';
import {
  type CellView,
  type ColumnType,
  type ColumnView,
  type NoteKind,
  type Reference,
  type RowView,
  type TabView,
} from './contract/schema';

const COLUMN_WIDTHS: Record<ColumnType, number> = {
  text: 240,
  number: 120,
  enum: 150,
  date: 130,
  reference: 200,
};

export const columnWidth = (column: ColumnView) => COLUMN_WIDTHS[column.type];

export const cellNote = (cell: CellView | undefined): { text: string; kind: NoteKind } | null =>
  cell && 'note' in cell && cell.note
    ? { text: cell.note, kind: cell.noteKind ?? 'assumption' }
    : null;

export const visibleSheetColumns = (tab: TabView) => tab.columns.filter((column) => !column.hidden);

export const peopleEditable = (column: ColumnView) => column.type !== 'reference';

type SheetVisibleRow = {
  row: RowView;
  number: number;
  hasChildren: boolean;
  collapsed: boolean;
};

export const visibleSheetRows = (tab: TabView, collapsed: ReadonlySet<string>) => {
  const visible: SheetVisibleRow[] = [];
  let hiddenBelow: number | null = null;
  tab.rows.forEach((row, index) => {
    if (hiddenBelow !== null && row.level > hiddenBelow) return;
    const next = tab.rows[index + 1];
    const hasChildren = !!next && next.level > row.level;
    const isCollapsed = hasChildren && collapsed.has(row.id);
    hiddenBelow = isCollapsed ? row.level : null;
    visible.push({ row, number: index + 1, hasChildren, collapsed: isCollapsed });
  });
  return visible;
};

const withDescendants = (tab: TabView, rowIds: string[]) => {
  const selected = new Set(rowIds);
  const block: string[] = [];
  let blockLevel: number | null = null;
  for (const row of tab.rows) {
    if (blockLevel !== null && row.level > blockLevel) {
      block.push(row.id);
      continue;
    }
    blockLevel = selected.has(row.id) ? row.level : null;
    if (blockLevel !== null) block.push(row.id);
  }
  return block;
};

const insertionPoint = (
  tab: TabView,
  rowId: string,
  position: 'above' | 'below',
  collapsed: ReadonlySet<string>,
) => {
  const index = tab.rows.findIndex((row) => row.id === rowId);
  const row = tab.rows[index]!;
  if (position === 'above') return { place: { before: rowId }, level: row.level };
  const next = tab.rows[index + 1];
  if (next && next.level > row.level && !collapsed.has(rowId))
    return { place: { after: rowId }, level: next.level };
  return { place: { after: withDescendants(tab, [rowId]).at(-1)! }, level: row.level };
};

export const rowPastePlace = (
  tab: TabView,
  rowId: string,
  position: 'above' | 'below',
  cutRowIds: string[],
) => {
  const cut = new Set(cutRowIds);
  return insertionPoint(
    { ...tab, rows: tab.rows.filter((row) => !cut.has(row.id)) },
    rowId,
    position,
    new Set([rowId]),
  ).place;
};

const numberSymbols = (locale: string) => {
  const parts = new Intl.NumberFormat(locale).formatToParts(-12345.6);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? '',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    minus: parts.find((part) => part.type === 'minusSign')?.value ?? '-',
  };
};

const parseSheetNumber = (text: string, locale: string) => {
  const { group, decimal, minus } = numberSymbols(locale);
  const spaced = group.trim() === '';
  let normalized = '';
  for (const character of text.trim()) {
    if (character === group || (spaced && character.trim() === '')) continue;
    if (character === decimal) normalized += '.';
    else if (character === minus) normalized += '-';
    else normalized += character;
  }
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
};

const matchOption = (column: ColumnView, text: string) =>
  column.options.find((option) => option.toLowerCase() === text.trim().toLowerCase());

export const parseSheetInput = (
  column: ColumnView,
  text: string,
  locale: string,
): { input: CellInput } | { clear: true } | { error: 'reference' | 'number' | 'option' } => {
  if (!peopleEditable(column)) return { error: 'reference' };
  const trimmed = text.trim();
  if (!trimmed) return { clear: true };
  if (column.type === 'number') {
    const value = parseSheetNumber(trimmed, locale);
    return value === null ? { error: 'number' } : { input: { value } };
  }
  if (column.type === 'enum') {
    const options = (column.multiple ? trimmed.split(',') : [trimmed]).map((part) =>
      matchOption(column, part),
    );
    return options.every((option) => option !== undefined)
      ? { input: { value: options } }
      : { error: 'option' };
  }
  if (column.type === 'date') return { input: { value: trimmed } };
  return { input: { value: text } };
};

const plainNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: 15 }).format(value);

export const cellClipboardText = (
  cell: CellView | undefined,
  locale: string,
  referenceText: (reference: Reference) => string,
): string => {
  if (!cell) return '';
  if (cell.type === 'invalid') return cell.raw;
  if (cell.type === 'note') return '';
  if (cell.type === 'number') return plainNumber(cell.value, locale);
  if (cell.type === 'enum') return cell.value.join(', ');
  if (cell.type === 'reference') return cell.value.map(referenceText).join('; ');
  return cell.value;
};

export const sheetRowLabel = (row: RowView, tab: TabView) => {
  for (const column of visibleSheetColumns(tab)) {
    const cell = row.cells[column.id];
    if (cell?.type === 'text' && cell.value.trim()) return cell.value.trim();
  }
  return null;
};
